import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import { ep } from '../../api/endpoints';
import type { BranchDeptLogic } from '../ProfitAndLossSearchFilters';

// ダイアログを開く際にテーブル側から渡される文脈（どの科目・補助科目・月をドリルダウンするか）
export type LedgerCellContext = {
  accountCode: string;
  accountName: string;
  subAccountCode?: string;
  subAccountName?: string;
  month: number | null; // null = 通期累計 (months 指定時はその月リスト)
  // 月リスト (四半期・半期など。month が null のときのみ有効。経営概況表の期間モード用)
  months?: number[];
  // months 指定時の期間表示ラベル (例 '1Q' '上期'。未指定時は '通期')
  periodLabel?: string;
  // true のとき前年度 (fiscalYear - 1) の仕訳明細を表示する (経営概況表の前年実績列用)
  prevYear?: boolean;
  amount: number;
};

type JournalDetail = {
  slip_id: number | null;
  slip_date: string | null;
  slip_no: string | null;
  target_month: number | null;
  section_name: string | null;
  sub_account_name: string | null;
  counter_account_name: string | null;
  counter_sub_account_name: string | null;
  supplier_name: string | null;
  description: string | null;
  debit_amount: number;
  credit_amount: number;
  is_allocation: boolean;
};

type LedgerResult = {
  details: JournalDetail[];
  total_debit: number;
  total_credit: number;
  net: number;
  count: number;
  allocation_total: number;
};

type Props = {
  context: LedgerCellContext | null;
  fiscalYear: number;
  branchCodes: string[];
  departmentCodes: string[];
  // 拠点×部署の結合ロジック (省略時 'and'=従来動作)。経営概況表の OR 絞り込みと発生額を一致させる
  branchDeptLogic?: BranchDeptLogic;
  onClose: () => void;
};

const fmt = (v: number): string => (v ? Math.round(v).toLocaleString() : '-');

/**
 * 補助元帳ドリルダウンダイアログ。
 *
 * 合計試算表の金額セルクリックで開き、その勘定科目（任意で補助科目）・期間の
 * 仕訳明細（trn_obc_journal_detail）を get_obc_journal_details から取得して表示する。
 * 当該科目が借方/貸方どちらに立つ明細も併せて、相手科目・摘要・借方/貸方金額で一覧する。
 */
export default function JournalLedgerDialog({
  context,
  fiscalYear,
  branchCodes,
  departmentCodes,
  branchDeptLogic = 'and',
  onClose,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LedgerResult | null>(null);

  const open = context !== null;
  // 前年実績列からのドリルダウン時は対象年度を1年ずらす
  const targetYear = context?.prevYear ? fiscalYear - 1 : fiscalYear;

  // 背面スクロールの自前ロック。
  // MUI 標準ロック (disableScrollLock 無し) は body へ padding-right を補填し背面右に白帯が出るため使わず、
  // scrollbar-gutter: stable でスクロールバーの溝を予約したまま overflow: hidden にすることで
  // 白帯もレイアウトのズレも出さずに背面のスクロールだけを禁止する。
  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const prevGutter = html.style.scrollbarGutter;
    const prevOverflow = html.style.overflow;
    html.style.scrollbarGutter = 'stable';
    html.style.overflow = 'hidden';
    return () => {
      html.style.scrollbarGutter = prevGutter;
      html.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!context) return;
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setResult(null);
      try {
        const res = await axios.post(ep('get_obc_journal_details'), {
          year: context.prevYear ? fiscalYear - 1 : fiscalYear,
          account_code: context.accountCode,
          sub_account_code: context.subAccountCode || null,
          month: context.month ?? null,
          months: context.month == null && context.months && context.months.length > 0 ? context.months : null,
          branch_codes: branchCodes.length > 0 ? branchCodes : null,
          department_codes: departmentCodes.length > 0 ? departmentCodes : null,
          branch_dept_logic: branchDeptLogic,
        });
        if (cancelled) return;
        if (res.data.status === 'error') {
          throw new Error(res.data.message || res.data.error || 'API側でエラーが発生しました');
        }
        setResult(res.data.data as LedgerResult);
      } catch (err: any) {
        if (!cancelled) setError(`通信エラー: ${err.message || '不明なエラー'}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [context, fiscalYear, branchCodes, departmentCodes, branchDeptLogic]);

  const periodLabel = context?.month != null
    ? `${context.month}月`
    : context?.months && context.months.length > 0
      ? `${context.periodLabel ?? '期間'}累計`
      : '通期累計';
  const subLabel = context?.subAccountName ? ` / ${context.subAccountName}` : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      // スクロールロックの padding-right 補填で背面の右側に白帯が出るため無効化する (theme.ts の Menu/Popover と同趣旨)
      disableScrollLock
      PaperProps={{ sx: { borderRadius: '14px', height: '82vh' } }}
    >
      <DialogTitle sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', p: 0.7, borderRadius: '8px', backgroundColor: '#eff6ff', color: '#3b82f6' }}>
            <Icons.ReceiptLong sx={{ fontSize: '22px' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '16px', lineHeight: 1.2 }}>
              補助元帳 — {context?.accountName}{subLabel}
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: '#64748b' }}>
              {targetYear}年度 {periodLabel} の仕訳明細{context?.prevYear ? '（前年実績）' : ''}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><Icons.Close fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* サマリー */}
        {result && !loading && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip size="small" label={`仕訳 ${result.details.filter((d) => !d.is_allocation).length.toLocaleString()}件`} sx={{ fontSize: '12px', bgcolor: '#f1f5f9' }} />
            <Chip size="small" label={`借方合計 ${result.total_debit.toLocaleString()}`} sx={{ fontSize: '12px', bgcolor: '#eff6ff', color: '#1e3a8a', fontWeight: 600 }} />
            <Chip size="small" label={`貸方合計 ${result.total_credit.toLocaleString()}`} sx={{ fontSize: '12px', bgcolor: '#fef2f2', color: '#991b1b', fontWeight: 600 }} />
            {result.allocation_total !== 0 && (
              <Chip size="small" label={`うち部門配賦 ${result.allocation_total.toLocaleString()}`} sx={{ fontSize: '12px', bgcolor: '#fffbeb', color: '#92400e', fontWeight: 600 }} />
            )}
            <Chip size="small" label={`発生額 ${result.net.toLocaleString()}`} sx={{ fontSize: '12px', bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 700 }} />
            <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>（税抜・合計試算表と一致）</Typography>
          </Box>
        )}

        {error && <Alert severity="warning" sx={{ borderRadius: '8px', mb: 1 }}>{error}</Alert>}
        {loading && <LoadingView message="仕訳明細を取得中..." height="40vh" size={50} />}

        {!loading && result && result.details.length > 0 && (
          <TableContainer component={Paper} sx={{ borderRadius: '10px', boxShadow: '0 1px 3px rgb(0 0 0 / 0.1)', overflow: 'auto', flex: 1, minHeight: 0 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['日付 / 伝票No', '部門', '取引先', '相手科目', '摘要', '借方', '貸方'].map((h, i) => (
                    <TableCell
                      key={h}
                      align={i >= 5 ? 'right' : 'left'}
                      sx={{ backgroundColor: '#f5f5f5', color: 'grey.600', fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {result.details.map((d, idx) => (
                  <TableRow
                    key={`${d.slip_id}-${idx}`}
                    hover
                    sx={d.is_allocation ? { backgroundColor: '#fffbeb', '& td': { fontStyle: 'italic' } } : undefined}
                  >
                    <TableCell sx={{ fontSize: '12.5px', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      <Box sx={{ color: '#475569' }}>{d.slip_date || '-'}</Box>
                      <Box sx={{ color: '#94a3b8', fontSize: '11.5px' }}>{d.slip_no || '-'}</Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '12.5px', color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      {d.section_name || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12.5px', color: '#334155', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      {d.supplier_name || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12.5px', color: d.is_allocation ? '#92400e' : '#334155', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      <Box>{d.counter_account_name || '-'}</Box>
                      {d.counter_sub_account_name ? (
                        <Box sx={{ color: '#94a3b8', fontSize: '11.5px' }}>{d.counter_sub_account_name}</Box>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12.5px', color: '#475569', minWidth: 220, whiteSpace: 'pre-wrap', wordBreak: 'break-word', verticalAlign: 'top' }}>
                      {d.description || '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '12.5px', fontWeight: 600, color: d.debit_amount ? '#1e3a8a' : '#cbd5e1', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{fmt(d.debit_amount)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '12.5px', fontWeight: 600, color: d.credit_amount ? '#991b1b' : '#cbd5e1', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{fmt(d.credit_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && result && result.details.length === 0 && !error && (
          <Box sx={{ mt: 4, p: 4, border: '2px dashed #cbd5e1', borderRadius: '12px', textAlign: 'center' }}>
            <Typography color="textSecondary">この条件に該当する仕訳明細はありません。対象月の仕訳明細同期が済んでいるかご確認ください。</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
